import {
  BulkChangeBillingPreviewRequest,
  BulkChangeBillingRequest,
  ReservationApi,
} from '@lib/state/api/generated/reservations';
import { getBulkChangeBillingStore, BulkChangeBillingStore } from './bulk-change-billing.store';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';
import { GetBulkChangeBillingRunsRequest } from './bulk-change-billing.model';
import { applyTransaction, setLoading } from '@datorama/akita';
import { dispatchForm, PaginatedQuery, PaginationState, MediaTypes, CommonForm } from '@lib/state';
import { BulkChangeBillingForms } from './bulk-change-billing.forms';
import { saveAs } from 'file-saver';

export class BulkChangeBillingService {
  constructor(private api: ReservationApi, private store: BulkChangeBillingStore) {}

  getRuns(
    {
      propertyId,
      corporateAccountId,
      status,
      recordNumber,
      roomTypeId,
    }: GetBulkChangeBillingRunsRequest,
    { continuationToken, limit }: PaginatedQuery
  ) {
    if (!propertyId) return;

    from(
      this.api.reservationBulkChangeBillingGet(
        propertyId,
        corporateAccountId,
        recordNumber,
        status,
        roomTypeId,
        continuationToken ?? undefined,
        limit ?? 20
      )
    )
      .pipe(
        map(data => data.data),
        setLoading(this.store)
      )
      .subscribe({
        next: ({ data, ...pagination }) =>
          applyTransaction(() => {
            if (continuationToken) {
              this.store.upsertMany(data);
            } else {
              this.store.set(data);
            }
            this.updatePaginationState(pagination);
            this.updateUI({
              propertyId,
              corporateAccountId,
              status,
              recordNumber,
              roomTypeId,
            });
          }),
        error: err => console.error('Failed to fetch bulk change billing runs', err),
      });
  }

  getRunById(id: string) {
    from(this.api.reservationBulkChangeBillingIdGet(id))
      .pipe(
        map(data => data.data.data),
        setLoading(this.store)
      )
      .subscribe({
        next: data =>
          applyTransaction(() => {
            this.store.upsert(data.id, data);
            this.store.setActive(id);
          }),
        error: err => console.error(`Failed to fetch bulk change billing run ${id}`, err),
      });
  }

  previewRun(request: BulkChangeBillingPreviewRequest) {
    from(this.api.reservationBulkChangeBillingPreviewPost(request))
      .pipe(
        map(response => response.data),
        dispatchForm(BulkChangeBillingForms.Preview)
      )
      .subscribe({
        next: data => this.store.update({ preview: data, pendingRunId: undefined }),
        error: err => console.error('Failed to preview bulk change billing run', err),
      });
  }

  createRun(request: BulkChangeBillingRequest) {
    from(this.api.reservationBulkChangeBillingPost(request))
      .pipe(
        map(({ data }) => data.data),
        dispatchForm(BulkChangeBillingForms.Create)
      )
      .subscribe({
        next: data =>
          applyTransaction(() => {
            this.store.upsert(data.runId, { id: data.runId } as any);
            this.store.update({ pendingRunId: data.runId ?? undefined });
          }),
        error: err => console.error('Failed to create bulk change billing run', err),
      });
  }

  exportRun(runId: string) {
    from(
      this.api.reservationBulkChangeBillingRunIdExportGet(runId, {
        responseType: 'arraybuffer', // Pass 'arraybuffer' as responseType in options
      })
    )
      .pipe(
        map(x => new Blob([(x.data as unknown) as BlobPart], { type: MediaTypes.ExcelFile })),
        dispatchForm(CommonForm.Export)
      )
      .subscribe(x => saveAs(x, `Bulk-Change-Billing-Run-${runId}.xlsx`));
  }

  selectRun(id: string) {
    this.store.setActive(id);
    return () => this.store.setActive(null);
  }

  reset() {
    this.store.reset();
  }

  private updatePaginationState(pagination: PaginationState) {
    this.store.update(() => ({
      pagination,
    }));
  }

  private updateUI(ui: GetBulkChangeBillingRunsRequest) {
    this.store.update(() => ({ ui }));
  }
}

let _bulkChangeBillingService: BulkChangeBillingService;

export function getBulkChangeBillingService(): BulkChangeBillingService {
  if (!_bulkChangeBillingService) {
    _bulkChangeBillingService = new BulkChangeBillingService(
      new ReservationApi(),
      getBulkChangeBillingStore()
    );
  }
  return _bulkChangeBillingService;
}
